// CSV / Excel export for tables and cards. XLSX is loaded on demand (separate chunk).

export interface ExportColumn {
  key: string;
  label: string;
}
export type ExportRow = Record<string, unknown>;
export interface ExportData {
  columns: ExportColumn[];
  rows: ExportRow[];
}

const cell = (v: unknown): string => {
  if (v == null) return '';
  const s = typeof v === 'number' ? String(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv({ columns, rows }: ExportData): string {
  const head = columns.map((c) => cell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => cell(r[c.key])).join(','));
  return [head, ...body].join('\n');
}

export function safeFileName(name: string): string {
  return (
    name
      .replace(/[^\w\- ]+/g, '')
      .trim()
      .replace(/\s+/g, '-') || 'export'
  ).toLowerCase();
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportCsv(data: ExportData, name: string) {
  download(new Blob(['﻿' + toCsv(data)], { type: 'text/csv;charset=utf-8' }), `${safeFileName(name)}.csv`);
}

export async function exportXlsx(data: ExportData, name: string) {
  const XLSX = await import('xlsx');
  const aoa: unknown[][] = [
    data.columns.map((c) => c.label),
    ...data.rows.map((r) => data.columns.map((c) => r[c.key] ?? null)),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31) || 'Sheet1');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  download(
    new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${safeFileName(name)}.xlsx`,
  );
}
